import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  FaChevronDown,
  FaSyncAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaTimes
} from 'react-icons/fa';
import CommonHeader from '../../util_components/common_header';
import Paginator from '../../util_components/paginator';
import DateRangeFilter from '../early_retention_checklist/DateRangeFilter';
import MultiSelectDropdown from '../early_retention_checklist/MultiSelectDropdown';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import { hasAccess } from '../../../util_functions';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import { INSTANT_GONE_EVENT } from './instant_alert_config';
import { subscribe_claim_changed } from './claim_broadcast';
import AssignPicker from './AssignPicker';
import * as actions from '../../../redux/action_creators';
import is_overdue, { is_terminal } from './overdue';
import {
  elapsed_ms,
  format_elapsed,
  timer_color_css
} from '../../util_components/instant_alert/instant_timer';
import { is_instant_live, short_label } from './instant';
import { write_persisted_filters } from './filters_storage';
import { readFiltersCollapsed, writeFiltersCollapsed } from './filters_collapse';
import { localTimeCell } from './local_time';
import { offHours, offHoursTooltip } from './off_hours';
import build_gen_config_payload, {
  DEFAULT_GLOBALS_BOUNDS,
  DEFAULT_LEVELS,
  DEFAULT_PRIORITIES,
  MAX_LEAD_MINUTES,
  MIN_LEAD_MINUTES,
  build_gen_globals_payload,
  has_globals_errors,
  has_rule_errors,
  parse_gen_globals,
  set_globals_field,
  set_lead_minutes,
  set_tier_field,
  validate_gen_globals
} from './gen_config';
import RuleRow from './RuleRow';
import { GenPageHeader, RulesSection } from './GenerationLayout';
import GlobalSettingsCard from './GlobalSettingsCard';
import rule_notes_lines from './rule_notes';
import InstantReport from './InstantReport';
import SalesUsersPanel from './sales_users/SalesUsersPanel';
import AdvisorsPanel from './advisors/AdvisorsPanel';
import SessionAllocationPanel from './session_allocation/SessionAllocationPanel';
import { allocationChip } from './session_allocation/task_allocation';
import { advisorFromSearch, advisorPagePath, canWriteAdvisors } from './advisors/advisors';
import TabPanel from '../../util_components/page_tabs/TabPanel';
import { tabForKey } from '../../util_components/page_tabs/page_tabs';
import {
  canAllocateSessions,
  canSeeMyLeads,
  canSeeTeamLeads,
  leadsTabPending,
  resolveTab,
  searchWithTab,
  tabList
} from './page_tabs';
import MyLeadsPage, { LeadsLoading } from './my_leads/MyLeadsPage';
import TeamLeadsPage from './my_leads/TeamLeadsPage';
import {
  CALL_FILTERS,
  STATUS_FILTERS,
  TYPE_OPTIONS,
  UNASSIGNED_VALUE,
  default_date_range,
  filter_query_params,
  initial_filter_state,
  filter_summary,
  pick_created_range,
  pick_due_range,
  switch_date_field,
  toggle_show_created
} from './filters';
import { createdListLine } from './created_time';
import { filterTzAbbr, pageFilterTz, resolveFilterTz } from './filter_tz';
import { fetch_me } from '../sales_team/api';
import {
  can_assign,
  level_from_me,
  level_options,
  normalize_scope,
  person_column,
  resolve_level,
  scope_options
} from './level';
import { group_breaks, sort_breaks } from './priority';
import { Muted, OwnerCell, PriorityBadge } from './ListBadges';
import { closed_by_line } from './closed_by';
import { Banner, Btn, Chip, LABEL_CLASS, Segmented, btnStyle } from './ui';
import {
  DUE_CAPTION,
  bandCountLabel,
  bodyState,
  callLabel,
  clearedFilters,
  dueKind,
  memberMeta,
  pageRange,
  shortStamp,
  statusMeta,
  taskCell,
  zoneLabel
} from './list_view';

// How long to wait after the last keystroke before asking the server for the
// searched page. Long enough that typing a name is one request, short enough
// that the list feels like it is tracking what you type.
const SEARCH_DEBOUNCE_MS = 300;

// Rows open in a separate tab now ("Open Task" is target="_blank"), so tasks get
// completed or skipped while this tab sits untouched and its list goes stale.
// Coming back to the tab refetches — but only if the data is at least this old,
// so alt-tabbing back and forth does not turn into a request per switch.
const FOCUS_REFRESH_STALE_MS = 5000;

// Trailing debounce for the fresh-lead realtime refetch, so a burst of relay
// packets (several signups landing in one sweep pass) collapses into a single
// list request. Mirrors the CRM listener on /reminders.
const RELAY_REFRESH_DEBOUNCE_MS = 500;

// How often the instant rows' elapsed timers advance. One second because the
// timer is rendered as HH:MM:SS — anything slower and the seconds column would
// visibly skip.
const TIMER_TICK_MS = 1000;

// Below lg the table becomes cards: each row is a 4-track grid (chips | status | text |
// due + actions) instead of eight columns. Same DOM at every width, so the explicit
// table roles keep it a table for assistive tech once display: grid drops the implicit ones.
const LIST_COLS = 8;
const LIST_ROW =
  'grid grid-cols-[auto_auto_minmax(0,1fr)_104px] gap-x-2.5 gap-y-1 border-x-0 border-b-0 border-t border-line-subtle p-3 lg:grid-cols-[52px_minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1.1fr)_120px_minmax(0,.9fr)_108px] lg:gap-y-0 lg:px-4 lg:py-2.5';
const LIST_HEAD_ROW =
  'flex flex-wrap items-center gap-x-4 gap-y-1 border-0 px-3 py-2 max-lg:before:text-[11px] max-lg:before:text-content-muted max-lg:before:content-["Sort_by"] lg:grid lg:grid-cols-[52px_minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1.1fr)_120px_minmax(0,.9fr)_108px] lg:items-start lg:gap-x-2.5 lg:px-4';
// p-0 and the explicit borders undo common.css's global td padding and all-side tr border.
const LIST_TD = 'block min-w-0 p-0 text-left align-top';
const LIST_TH = 'block p-0 text-left font-normal';
// Header text of each sortable column, for the "Other tasks — by" run label.
const SORT_LABELS = {
  member: 'Member',
  task: 'Task',
  due_datetime: 'Due',
  local_time: 'Their time',
  status: 'Status',
  created: 'Created'
};
// Card placement (base) and table placement (lg), per column.
const POS = {
  pri: { card: 'col-start-1 row-start-3', lg: 'lg:col-start-1 lg:row-start-1' },
  member: {
    card: 'col-start-1 col-span-3 row-start-1',
    lg: 'lg:col-start-2 lg:col-span-1 lg:row-start-1'
  },
  task: {
    card: 'col-start-1 col-span-3 row-start-2',
    lg: 'lg:col-start-3 lg:col-span-1 lg:row-start-1'
  },
  due: {
    card: 'col-start-4 row-start-1 row-span-2 text-right',
    lg: 'lg:col-start-4 lg:row-start-1 lg:row-span-1 lg:text-left'
  },
  local: {
    card: 'col-start-1 col-span-3 row-start-4',
    lg: 'lg:col-start-5 lg:col-span-1 lg:row-start-1'
  },
  status: {
    card: 'col-start-2 col-span-2 row-start-3',
    lg: 'lg:col-start-6 lg:col-span-1 lg:row-start-1'
  },
  person: {
    card: 'col-start-1 col-span-3 row-start-5',
    lg: 'lg:col-start-7 lg:col-span-1 lg:row-start-1'
  },
  action: {
    card: 'col-start-4 row-start-3 row-span-3',
    lg: 'lg:col-start-8 lg:row-start-1 lg:row-span-1'
  }
};
// Open task over Assign, full width, never wrapping; AssignPicker's own root and trigger
// are stretched from outside so the shared picker stays untouched.
const ACTION_STACK =
  'flex flex-col items-stretch gap-1.5 [&>div>button]:!h-7 [&>div>button]:w-full [&>div]:!block max-lg:[&>div>button]:!h-10';
// Phones: a segmented control fills the width and wraps into even cells; up to four
// short options (Level, Claim) keep one line.
const SEG_WRAP =
  'max-sm:w-full max-sm:[&>div]:grid max-sm:[&>div]:w-full max-sm:[&>div]:grid-cols-[repeat(auto-fit,minmax(84px,1fr))] max-sm:[&_button]:!whitespace-normal max-sm:[&_button]:!px-2';
const SEG_WRAP_SHORT =
  'max-sm:w-full max-sm:[&>div]:grid max-sm:[&>div]:w-full max-sm:[&>div]:grid-cols-[repeat(auto-fit,minmax(60px,1fr))] max-sm:[&_button]:!whitespace-normal max-sm:[&_button]:!px-2';
// Dropdown triggers lose common.css's button margin; on phones they fill the width at 40px.
const DROPDOWN_WRAP =
  '[&>div>button]:!m-0 max-sm:w-full max-sm:[&>div>button]:!box-border max-sm:[&>div>button]:!min-h-[40px] max-sm:[&>div>button]:!w-full';
const DATE_FIELDS = [
  { label: 'Due', value: 'due' },
  { label: 'Created', value: 'created' }
];
const TYPE_LABELS = new Map(TYPE_OPTIONS.map((o) => [o.value, o.label]));

// Sales task list — one row per (student, sales task). Filters: level, scope,
// date range, task type, status, the L3 owner dropdown and a type-to-search box
// on the student's first name.
class SalesTaskList extends Component {
  // The logged-in cms_user's uuid (from the ops JWT), matched against each
  // task's claimed_by_ops_uuid.
  current_cms_uuid = getAuthUserInfoOrDefault().uuid || '';

  state = {
    filters_collapsed: readFiltersCollapsed(),
    items: [],
    // Total rows matching the current filters (server-reported), for the
    // pagination controls. items holds only the current page.
    total: 0,
    // L3 owner dropdown options ([{uuid, name}]) and whether any task has no
    // owner — both from the list response.
    owners: [],
    has_no_owner: false,
    // {start, end} HH:MM from the list response; null until it lands (or on a
    // server that does not send it), when call_window() falls back to /me.
    call_window: null,
    // True until the first /list lands; /me is fetched before it.
    loading: true,
    // The last /list failure reason; '' once a load succeeds.
    load_error: '',
    // GET /v2/sales_team/me; null until loaded or when it failed.
    me: null,
    me_loaded: false,
    // L1 | L2 | L3 | ALL. Not persisted; null until /me resolves, and no list
    // request is made before then.
    level: null,
    // The persisted filters (status, type, scope, owner, due-date range) plus
    // the date-range "touched" flag. Restored from localStorage when present,
    // otherwise seeded with the defaults (rolling window in the header timezone).
    // eslint-disable-next-line react/destructuring-assignment
    ...initial_filter_state(this.props.filter_tz),
    // Type-to-search on the student's first name. Deliberately NOT part of the
    // persisted filter set: a search is a momentary "where is X", not a working
    // preference, and restoring one on the next visit would look like an empty
    // task list. Resolved server-side (see search_timer / set_search).
    search: '',
    gen_rules: [],
    gen_min_day: 0,
    gen_max_day: 29,
    // Whether THIS user may edit the generation rules (server-reported; only a
    // SUPER_USER may). Everyone can see the section either way — non-super-users
    // get it read-only. Defaults to false so the controls are never briefly
    // live before the config response lands.
    gen_can_edit: false,
    // Bounds for the per-rule "refresh every N hours" / "due in N hours"
    // inputs (server-supplied; these are only the pre-load fallbacks).
    gen_min_refresh_hours: 0,
    gen_max_refresh_hours: 720,
    gen_min_due_offset_hours: 0,
    gen_max_due_offset_hours: 720,
    // Bounds for the per-rule instant "expiry after N minutes" / "dedup window
    // of N hours" inputs (server-supplied; these are only the pre-load
    // fallbacks, matching MinExpiryMinutes/MaxExpiryMinutes and
    // MinDedupWindowHours/MaxDedupWindowHours).
    gen_min_expiry_minutes: 1,
    gen_max_expiry_minutes: 10080,
    gen_min_dedup_hours: 0,
    gen_max_dedup_hours: 720,
    // Bounds for the per-rule "grace of N minutes" input, shown on the rules
    // that declare has_grace (today only LEAD_OTP_NOT_VERIFIED). Matches
    // MinGraceMinutes/MaxGraceMinutes.
    gen_min_grace_minutes: 0,
    gen_max_grace_minutes: 1440,
    // Bounds for the per-rule "delay of N minutes" input, shown on the rules
    // that declare has_delay (the delayed-check friction rules). Matches
    // MinDelayMinutes/MaxDelayMinutes.
    gen_min_delay_minutes: 1,
    gen_max_delay_minutes: 1440,
    // Per-rule lookback bounds (has_lookback). Matches
    // MinLookbackHours/MaxLookbackHours.
    gen_min_lookback_hours: 1,
    gen_max_lookback_hours: 168,
    // Lead-time bounds for has_lead rules (the joining call). Matches
    // MinLeadMinutes/MaxLeadMinutes.
    gen_min_lead_minutes: MIN_LEAD_MINUTES,
    gen_max_lead_minutes: MAX_LEAD_MINUTES,
    // Server-supplied coverage notes for the Task Generation card (rule_notes
    // on the generation_config GET). Null until the config lands.
    gen_rule_notes: null,
    // Tier option lists and the Global settings card (null until a server
    // that sends globals answers; then the card renders and saves patches).
    gen_levels: DEFAULT_LEVELS,
    gen_priorities: DEFAULT_PRIORITIES,
    gen_globals: null,
    gen_globals_bounds: DEFAULT_GLOBALS_BOUNDS,
    saving_config: false,
    config_dirty: false,
    generating: false,
    sort_key: 'priority',
    sort_dir: 'asc',
    page: 1,
    // Bumped once a second so the instant rows' elapsed timers re-render. Held
    // in state (not on the instance) precisely because it must cause a render;
    // nothing reads its value.
    tick: 0
  };

  page_size = 20;

  // Pending debounce for the search box (see set_search). Held on the instance
  // rather than in state — it drives no rendering, and touching state per
  // keystroke would re-render the whole table for nothing.
  search_timer = null;

  // When the last /list response landed, and whether one is in flight. Both feed
  // refresh_on_focus only — they drive no rendering, so they stay off state.
  last_loaded_at = 0;

  loading_in_flight = false;

  list_seq = 0;

  // Pending debounce for the fresh-lead relay refetch (see on_relay_item).
  // Instance field for the same reason as search_timer: it drives no rendering.
  relay_refresh_timer = null;

  // Per-second interval driving state.tick (see TIMER_TICK_MS).
  tick_timer = null;

  // The ?advisor= deep link already replaced, so a re-render does not replace again.
  advisor_redirected = '';

  componentDidMount() {
    this.load_me();
    this.load_gen_config();
    this.tick_timer = setInterval(
      () => this.setState((prev) => ({ tick: prev.tick + 1 })),
      TIMER_TICK_MS
    );
    window.addEventListener('focus', this.refresh_on_focus);
    document.addEventListener('visibilitychange', this.refresh_on_focus);
    this.redirect_advisor_link();
    CallbackHandler.shared.registerRelayItemListener(this.on_relay_item);
    window.addEventListener(INSTANT_GONE_EVENT, this.schedule_relay_refresh);
    this.unsubscribe_claims = subscribe_claim_changed(this.schedule_relay_refresh);
  }

  componentWillUnmount() {
    // A debounce that fires after unmount would setState on a dead component.
    if (this.search_timer) clearTimeout(this.search_timer);
    if (this.relay_refresh_timer) clearTimeout(this.relay_refresh_timer);
    if (this.tick_timer) clearInterval(this.tick_timer);
    window.removeEventListener('focus', this.refresh_on_focus);
    document.removeEventListener('visibilitychange', this.refresh_on_focus);
    CallbackHandler.shared.unregisterRelayItemListener(this.on_relay_item);
    window.removeEventListener(INSTANT_GONE_EVENT, this.schedule_relay_refresh);
    if (this.unsubscribe_claims) this.unsubscribe_claims();
  }

  // A fresh-lead relay means a FRESH_LEAD row was just inserted server-side, so
  // the open list is stale the moment the packet lands. Refetch with the current
  // filters — trailing-debounced so a burst of signups is one request. The app
  // shell (home/index.jsx) owns the desktop notification for the same packet;
  // this listener only keeps the visible table live. `instant_task_cleared` and
  // a lost claim (INSTANT_GONE_EVENT) mean a listed row changed status; a
  // released, expired or assigned hold (claim_broadcast, any tab) changed its claimer.
  on_relay_item = (item) => {
    const type = item && item.data && item.data.type;
    if (type !== 'fresh_lead' && type !== 'instant_task_cleared') return;
    this.schedule_relay_refresh();
  };

  on_assigned = (res, member) => {
    const { set_notification_variable } = this.props;
    const name = res?.claimed_by_name || member?.name || 'rep';
    set_notification_variable(true, 'success', `Assigned to ${name}`);
    this.load_data();
  };

  schedule_relay_refresh = () => {
    if (this.relay_refresh_timer) clearTimeout(this.relay_refresh_timer);
    this.relay_refresh_timer = setTimeout(() => {
      this.relay_refresh_timer = null;
      this.load_data();
    }, RELAY_REFRESH_DEBOUNCE_MS);
  };

  // Refetch when the agent returns to this tab, because the task they just worked
  // in the other tab is no longer pending. Deliberately cheap: nothing happens
  // while the tab is hidden, while a request is already running, or while the
  // current data is still fresh — the two listeners overlap (a tab switch fires
  // both), and the staleness guard is what keeps that to one request.
  refresh_on_focus = () => {
    if (document.visibilityState === 'hidden') return;
    if (this.loading_in_flight) return;
    if (Date.now() - this.last_loaded_at < FOCUS_REFRESH_STALE_MS) return;
    // A pending search debounce is about to reload with a newer term anyway.
    if (this.search_timer) return;
    this.load_data();
  };

  // Persist the filters whenever any of them changes. Driven off state rather
  // than wired into each setter so a future filter setter can't forget to save.
  // Selections are replaced (never mutated), so the identity comparison is sound.
  componentDidUpdate(prev_props, prev_state) {
    const watched = [
      'status_filter',
      'type_filter',
      'scope_filter',
      'owner_filter',
      'call_filter',
      'date_from',
      'date_to',
      'date_range_touched',
      'show_created',
      'created_from',
      'created_to'
    ];
    if (watched.some((k) => prev_state[k] !== this.state[k])) this.persist_filters();
    // Header timezone changed: same calendar days, re-read in the new zone.
    const { filter_tz } = this.props;
    if (prev_props.filter_tz !== filter_tz) {
      this.setState({ page: 1 }, this.reload);
    }
    this.redirect_advisor_link();
  }

  persist_filters = () => {
    const {
      status_filter,
      type_filter,
      scope_filter,
      owner_filter,
      call_filter,
      date_from,
      date_to,
      date_range_touched,
      show_created,
      created_from,
      created_to
    } = this.state;
    write_persisted_filters({
      status_filter,
      type_filter,
      scope_filter,
      owner_filter,
      call_filter,
      // null = still the rolling default, so it re-seeds from today next time.
      date_range: date_range_touched ? { from: date_from, to: date_to } : null,
      show_created,
      created_range: { from: created_from, to: created_to }
    });
  };

  load_gen_config = () => {
    const url = get_api_url('/v2/sales_task_list/generation_config');
    get_api(url, true)
      .then((res) => {
        const d = res.data.data || {};
        this.setState({
          gen_rules: d.rules || [],
          // Server-reported edit permission. The save/run endpoints enforce it
          // themselves (403 for a non-super-user), so this only decides whether
          // the controls are live — it is not the gate.
          gen_can_edit: !!d.can_edit,
          gen_min_day: d.min_day || 0,
          gen_max_day: d.max_day || 29,
          gen_min_refresh_hours: d.min_refresh_hours ?? 0,
          gen_max_refresh_hours: d.max_refresh_hours ?? 720,
          gen_min_due_offset_hours: d.min_due_offset_hrs ?? 0,
          gen_max_due_offset_hours: d.max_due_offset_hrs ?? 720,
          gen_min_expiry_minutes: d.min_expiry_minutes ?? 1,
          gen_max_expiry_minutes: d.max_expiry_minutes ?? 10080,
          gen_min_dedup_hours: d.min_dedup_hours ?? 0,
          gen_max_dedup_hours: d.max_dedup_hours ?? 720,
          gen_min_grace_minutes: d.min_grace_minutes ?? 0,
          gen_max_grace_minutes: d.max_grace_minutes ?? 1440,
          gen_min_delay_minutes: d.min_delay_minutes ?? 1,
          gen_max_delay_minutes: d.max_delay_minutes ?? 1440,
          gen_min_lookback_hours: d.min_lookback_hours ?? 1,
          gen_max_lookback_hours: d.max_lookback_hours ?? 168,
          gen_min_lead_minutes: d.min_lead_minutes ?? MIN_LEAD_MINUTES,
          gen_max_lead_minutes: d.max_lead_minutes ?? MAX_LEAD_MINUTES,
          // Product-owned sentences about PRD triggers that are covered by an
          // existing rule instead of a card of their own. Served with the
          // config so the wording lives in one repo; a server that does not
          // send them renders no note at all.
          gen_rule_notes: d.rule_notes || null,
          gen_levels: Array.isArray(d.levels) && d.levels.length ? d.levels : DEFAULT_LEVELS,
          gen_priorities:
            Array.isArray(d.priorities) && d.priorities.length ? d.priorities : DEFAULT_PRIORITIES,
          gen_globals: parse_gen_globals(d.globals),
          gen_globals_bounds: {
            min_claim_idle_minutes:
              d.min_claim_idle_minutes ?? DEFAULT_GLOBALS_BOUNDS.min_claim_idle_minutes,
            max_claim_idle_minutes:
              d.max_claim_idle_minutes ?? DEFAULT_GLOBALS_BOUNDS.max_claim_idle_minutes,
            min_unclaimed_buffer_minutes:
              d.min_unclaimed_buffer_minutes ?? DEFAULT_GLOBALS_BOUNDS.min_unclaimed_buffer_minutes,
            max_unclaimed_buffer_minutes:
              d.max_unclaimed_buffer_minutes ?? DEFAULT_GLOBALS_BOUNDS.max_unclaimed_buffer_minutes
          },
          config_dirty: false
        });
      })
      .catch(() => {
        /* config is optional context; failure is non-blocking */
      });
  };

  set_rule = (task_type, patch) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) => (r.task_type === task_type ? { ...r, ...patch } : r)),
      config_dirty: true
    }));
  };

  // Tier edits are tracked per field so the save sends only what changed.
  set_rule_tier = (task_type, field, value) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) =>
        r.task_type === task_type ? set_tier_field(r, field, value) : r
      ),
      config_dirty: true
    }));
  };

  set_rule_lead = (task_type, raw) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) =>
        r.task_type === task_type ? set_lead_minutes(r, raw) : r
      ),
      config_dirty: true
    }));
  };

  // Lead time is kept unclamped (RuleRow shows the error); an out-of-range one blocks Save.
  lead_bounds = () => {
    const { gen_min_lead_minutes: min, gen_max_lead_minutes: max } = this.state;
    return { min, max };
  };

  set_gen_global = (key, value) => {
    this.setState((prev) =>
      prev.gen_globals
        ? { gen_globals: set_globals_field(prev.gen_globals, key, value), config_dirty: true }
        : null
    );
  };

  // Numeric hour inputs (refresh rate / due offset). Kept as a number and
  // clamped to the server-supplied bounds; a cleared input reads as the min.
  set_rule_hours = (task_type, field, raw, min, max) => {
    let n = parseInt(raw, 10);
    if (Number.isNaN(n)) n = min;
    n = Math.min(Math.max(n, min), max);
    this.set_rule(task_type, { [field]: n });
  };

  // Optional numeric inputs (instant expiry minutes / dedup window hours).
  // Unlike set_rule_hours, a cleared box stays cleared instead of snapping to
  // the minimum: blank is a meaningful value for both fields (no expiry / fall
  // back to the server default), and snapping expiry up to its 1-minute
  // minimum would silently give the rule a one-minute lifetime.
  set_rule_optional = (task_type, field, raw, min, max) => {
    if (raw === '' || raw === null || raw === undefined) {
      this.set_rule(task_type, { [field]: '' });
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    this.set_rule(task_type, { [field]: Math.min(Math.max(n, min), max) });
  };

  // Select-all checkbox: tick = every day slot, untick = none.
  toggle_all_rule_days = (task_type, select_all) => {
    const { gen_min_day, gen_max_day } = this.state;
    const days = [];
    if (select_all) {
      for (let d = gen_min_day; d <= gen_max_day; d += 1) days.push(d);
    }
    this.set_rule(task_type, { days });
  };

  // Toggle one day chip in a rule's day set (kept sorted for display).
  toggle_rule_day = (task_type, day) => {
    this.setState((prev) => ({
      gen_rules: prev.gen_rules.map((r) => {
        if (r.task_type !== task_type) return r;
        const days = (r.days || []).includes(day)
          ? (r.days || []).filter((d) => d !== day)
          : [...(r.days || []), day].sort((a, b) => a - b);
        return { ...r, days };
      }),
      config_dirty: true
    }));
  };

  // Persist all generation rules (explicit Save).
  save_gen_config = () => {
    const { gen_rules, gen_globals, gen_globals_bounds } = this.state;
    if (has_globals_errors(validate_gen_globals(gen_globals))) return;
    if (has_rule_errors(gen_rules, this.lead_bounds())) return;
    const rules = build_gen_config_payload(gen_rules, this.lead_bounds());
    // A patch: {} (nothing loaded or touched) is omitted, keeping every stored value.
    const globals = build_gen_globals_payload(gen_globals, gen_globals_bounds);
    const config = Object.keys(globals).length ? { rules, globals } : { rules };
    this.setState({ saving_config: true });
    const url = get_api_url('/v2/sales_task_list/generation_config');
    post_api(url, { config }, true)
      .then(() => {
        this.props.set_notification_variable(true, 'success', 'Generation config saved');
        this.setState({ saving_config: false });
        this.load_gen_config();
      })
      .catch((e) => {
        let err = 'Failed to save config';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ saving_config: false });
      });
  };

  // Manually kick the generator (runs async server-side, ignoring the per-rule
  // refresh rates).
  run_generate = () => {
    this.setState({ generating: true });
    const url = get_api_url('/v2/sales_task_list/run_generate');
    post_api(url, {}, true)
      .then(() => {
        this.props.set_notification_variable(
          true,
          'success',
          'Generation started — refresh the list in a minute'
        );
      })
      .catch((e) => {
        let err = 'Failed to start generation';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
      })
      .finally(() => this.setState({ generating: false }));
  };

  // Build the /list query string from the current filter/sort/page state.
  // Filtering, sorting and pagination are all server-side now, so paging is
  // correct over the full table (not a capped client-side window).
  //
  // The filters are serialised by the shared helper (filters.js), which the
  // task form's "Done and Next" also uses so both walk the same set.
  build_query = () => {
    const { sort_key, sort_dir } = this.state;
    const { filter_tz } = this.props;
    const params = filter_query_params({ ...this.state, tz: filter_tz });
    params.set('page', String(this.state.page));
    params.set('limit', String(this.page_size));
    params.set('sort_key', sort_key);
    params.set('sort_dir', sort_dir);
    return params.toString();
  };

  // The level defaults from the caller's team level, so /me must land before
  // the first list request. A failed /me falls back to L1 (or a valid ?level=).
  load_me = () => {
    const { location, access_type } = this.props;
    const settle = (me) =>
      this.setState(
        { me, me_loaded: true, level: resolve_level(location?.search || '', me, access_type) },
        this.load_data
      );
    fetch_me()
      .then((me) => settle(me))
      .catch(() => settle(null));
  };

  load_data = () => {
    const { level: requested } = this.state;
    if (!requested) return;
    this.list_seq += 1;
    const seq = this.list_seq;
    const is_stale = () => seq !== this.list_seq;
    this.loading_in_flight = true;
    this.setState({ loading: true });
    const url = get_api_url(`/v2/sales_task_list/list?${this.build_query()}`);
    get_api(url, true)
      .then((res) => {
        if (is_stale()) return;
        const d = res.data.data || {};
        const items = d.items || [];
        const next = {
          items,
          call_window: d.call_window || null,
          total: typeof d.total === 'number' ? d.total : items.length,
          owners: d.owners || [],
          has_no_owner: !!d.has_no_owner,
          loading: false,
          load_error: ''
        };
        // The server may narrow the requested level; the controls follow the rows.
        if (d.applied_level && d.applied_level !== requested) {
          next.level = d.applied_level;
          const { sort_key } = this.state;
          if (['claimed', 'owner'].includes(sort_key)) {
            next.sort_key = person_column(d.applied_level).sort_key;
          }
        }
        this.setState(next);
      })
      .catch((e) => {
        if (is_stale()) return;
        let err = 'Failed to load sales tasks';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false, load_error: err });
      })
      .finally(() => {
        // The newer request owns the in-flight flag.
        if (is_stale()) return;
        // Stamped on failure too: a focus loop that retried a failing endpoint
        // every switch would be worse than showing stale rows.
        this.loading_in_flight = false;
        this.last_loaded_at = Date.now();
      });
  };

  // Href for a task's detail view. Rendered as a real <Link>/<a> with
  // target="_blank" (see render_row) rather than a history.push handler so the
  // list survives the agent working a task: the queue they were browsing stays
  // put, scrolled and filtered, in this tab. Being an anchor also means
  // cmd/ctrl-click, middle-click and "Open in new window" behave natively —
  // window.open in an onClick would break all three and trip popup blockers.
  //
  // ?from=list is kept: it is what makes the form render its "Back to list" link
  // and send the list's filters with "Done and Next" (see form.jsx).
  //
  // The persisted filters reach the form through localStorage, which crosses
  // tabs; the search term deliberately does NOT persist (see `search` in state),
  // so it would be invisible to a form opened in a new tab and "Done and Next"
  // would walk outside the searched set. It therefore rides along in the href —
  // but only when a search is actually active. Level (never persisted), scope and
  // the L3 owner filter ride along too, pinning the walk to what this list shows.
  form_link = (item) => {
    const { search: raw_search, level, scope_filter, owner_filter, call_filter } = this.state;
    const { filter_tz } = this.props;
    const params = new URLSearchParams({ uuid: item.uuid, from: 'list' });
    const search = (raw_search || '').trim();
    if (search) params.set('search', search);
    if (level) params.set('level', level);
    params.set('scope', normalize_scope(scope_filter, level));
    if (level === 'L3' && owner_filter.length) params.set('owner', owner_filter.join(','));
    if (call_filter) params.set('call', call_filter);
    // The form's tab has its own (default) header zone, so the list's rides along.
    if (filter_tz) params.set('tz', resolveFilterTz(filter_tz));
    return `/sales-task-form?${params.toString()}`;
  };

  // Reload the current page from the server. Used as the setState callback of
  // every filter/sort/page setter (all filtering is server-side now).
  reload = () => this.load_data();

  table_ref = React.createRef();

  // A new page starts at the column headers, not wherever the last page was scrolled to.
  set_page = (page) =>
    this.setState({ page }, () => {
      this.reload();
      // Optional call: jsdom has no scrollIntoView.
      this.table_ref.current?.scrollIntoView?.({ block: 'start' });
    });

  // Filter changes reset to the first page so the user isn't stranded on an
  // out-of-range page, then reload from the server.
  set_status_filter = (status_filter) => this.setState({ status_filter, page: 1 }, this.reload);

  // Receives the full next selection ([] = all types) and replaces it, never
  // mutates — which is what makes componentDidUpdate's identity check sound.
  set_type_filter = (type_filter) => this.setState({ type_filter, page: 1 }, this.reload);

  // Picking or clearing the range marks it touched, which is what makes it
  // persist as absolute dates instead of re-seeding as the rolling default.
  // Picking a due range resets the created range, and vice versa (pick_* in filters.js).
  set_date_range = (range) => this.setState({ ...pick_due_range(range), page: 1 }, this.reload);

  clear_date_range = () =>
    this.setState({ ...pick_due_range({ from: '', to: '' }), page: 1 }, this.reload);

  set_created_range = (range) =>
    this.setState({ ...pick_created_range(range), page: 1 }, this.reload);

  clear_created_range = () =>
    this.setState({ ...pick_created_range({ from: '', to: '' }), page: 1 }, this.reload);

  // The one picker writes whichever date the Due / Created switch selects.
  set_picked_range = (range) =>
    this.state.date_field === 'created'
      ? this.set_created_range(range)
      : this.set_date_range(range);

  clear_picked_range = () =>
    this.state.date_field === 'created' ? this.clear_created_range() : this.clear_date_range();

  set_date_field = (field) => {
    const patch = switch_date_field(this.state, field);
    if (!patch) return;
    const had_range = Boolean(
      this.state.date_from || this.state.date_to || this.state.created_from || this.state.created_to
    );
    if (!had_range) {
      this.setState(patch);
      return;
    }
    this.setState({ ...patch, page: 1 }, this.reload);
  };

  // Showing the created line keeps the page; hiding it drops an active created
  // filter or created sort, which reloads from page 1.
  set_show_created = (show) => {
    const { created_from, created_to, sort_key } = this.state;
    const drop_sort = !show && sort_key === 'created';
    const reload = drop_sort || (!show && Boolean(created_from || created_to));
    // The due range was cleared when the created range took over; give back the rolling window.
    const rolling = default_date_range(this.props.filter_tz);
    const patch = {
      ...toggle_show_created(show),
      ...(drop_sort ? { sort_key: 'priority', sort_dir: 'asc' } : {}),
      ...(!show && (created_from || created_to)
        ? { date_from: rolling.from, date_to: rolling.to, date_range_touched: false }
        : {})
    };
    if (!reload) {
      this.setState(patch);
      return;
    }
    this.setState({ ...patch, page: 1 }, this.reload);
  };

  // The person column (and so its sort key) follows the level.
  set_level = (level) =>
    this.setState(
      (prev) => ({
        level,
        page: 1,
        sort_key: ['claimed', 'owner'].includes(prev.sort_key)
          ? person_column(level).sort_key
          : prev.sort_key
      }),
      this.reload
    );

  set_scope_filter = (scope_filter) => this.setState({ scope_filter, page: 1 }, this.reload);

  // '' = any.
  set_call_filter = (call_filter) => this.setState({ call_filter, page: 1 }, this.reload);

  // An empty selection means "all owners" — clearing is a real choice and is
  // never re-seeded.
  set_owner_filter = (owner_filter) => this.setState({ owner_filter, page: 1 }, this.reload);

  // Type-to-search on the student's first name. The input is controlled, so the
  // typed value lands in state immediately (no lag while typing) but the request
  // is debounced: a burst of keystrokes fires ONE list call, not one per letter.
  //
  // Like every other filter this resets to page 1 — a search that kept the
  // current page would land the agent on an out-of-range page of a smaller
  // result set and look empty.
  set_search = (search) => {
    if (this.search_timer) clearTimeout(this.search_timer);
    this.setState({ search, page: 1 });
    this.search_timer = setTimeout(() => {
      this.search_timer = null;
      this.reload();
    }, SEARCH_DEBOUNCE_MS);
  };

  // Clearing is immediate — an explicit "×" is a decision, not typing, so there
  // is nothing to wait for.
  clear_search = () => {
    if (this.search_timer) clearTimeout(this.search_timer);
    this.search_timer = null;
    this.setState({ search: '', page: 1 }, this.reload);
  };

  // Empty state's "Clear filters": one reload, not one per filter setter.
  clear_filters = () => {
    if (this.search_timer) clearTimeout(this.search_timer);
    this.search_timer = null;
    this.setState(clearedFilters(this.props.filter_tz), this.reload);
  };

  // L3 "Owner" options come from the list response's owner list, plus a
  // "No owner" option when the server reports any ownerless task. Selected
  // values are force-included so the dropdown never renders a raw uuid.
  owner_options = () => {
    const { owners, has_no_owner, owner_filter } = this.state;
    const options = (owners || [])
      .map((o) => ({ value: o.uuid, label: o.name || o.uuid }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const self = this.current_cms_uuid;
    if (self && owner_filter.includes(self) && !options.some((o) => o.value === self)) {
      options.unshift({ value: self, label: 'Me' });
    }
    if (has_no_owner || owner_filter.includes(UNASSIGNED_VALUE)) {
      options.unshift({ value: UNASSIGNED_VALUE, label: 'No owner' });
    }
    return options;
  };

  // Click a sortable column header: toggle direction if same key, else asc,
  // then reload the (re-sorted) first page from the server. The server ignores
  // sort_dir for priority, so priority is always asc.
  toggle_sort = (sort_key) =>
    this.setState(
      (prev) => ({
        sort_key,
        sort_dir:
          sort_key !== 'priority' && prev.sort_key === sort_key && prev.sort_dir === 'asc'
            ? 'desc'
            : 'asc',
        page: 1
      }),
      this.reload
    );

  // Age of a live instant task, coloured by the PRD thresholds (black under ten
  // minutes, amber to thirty, red past it). Re-rendered every second by
  // state.tick. Returns null when the row has no parseable creation instant, so
  // the due cell falls back to the timestamp rather than showing 00:00:00.
  render_elapsed = (item) => {
    const ms = elapsed_ms(item.created_at_iso, Date.now());
    if (ms === null) return null;
    return (
      <span
        style={{
          color: timer_color_css(ms),
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {format_elapsed(ms)}
      </span>
    );
  };

  render_claimer = (item) => {
    if (item.claimed_by_ops_uuid && item.claimed_by_ops_uuid === this.current_cms_uuid) {
      return <span className="font-semibold text-content-primary">You</span>;
    }
    if (item.claimed_by_ops_uuid) return item.claimed_by_name || 'Claimed';
    return <Muted>Unclaimed</Muted>;
  };

  // L1/L2: who holds the task. L3: who owns the student. ALL: the holder, plus
  // the owner on L3 rows.
  render_person = (item) => {
    const { level } = this.state;
    if (level === 'L3') return <OwnerCell item={item} />;
    if (level === 'ALL' && item.level === 'L3') {
      return (
        <>
          <span>{this.render_claimer(item)}</span>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11.5px] text-content-muted">
            Owner <OwnerCell item={item} />
          </div>
        </>
      );
    }
    return this.render_claimer(item);
  };

  // Uppercase column label that sorts on click; `minor` is the second, sentence-case
  // label stacked in a merged header. Priority has a fixed direction, so no arrow while active.
  render_sort_label = (label, key, { fixed_dir = false, minor = false, title } = {}) => {
    const active = this.state.sort_key === key;
    const asc = this.state.sort_dir === 'asc';
    let Icon = FaSort;
    if (active) Icon = asc ? FaSortUp : FaSortDown;
    if (active && fixed_dir) Icon = null;
    return (
      <span
        key={key}
        role="button"
        tabIndex={0}
        className={`inline-flex cursor-pointer select-none items-center gap-1 max-lg:min-h-[32px] ${
          minor
            ? 'text-[11px] font-semibold normal-case tracking-normal'
            : 'text-[10.5px] font-bold uppercase tracking-[.04em]'
        } ${active ? 'text-content-primary' : 'text-content-muted'}`}
        onClick={() => this.toggle_sort(key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle_sort(key);
          }
        }}
        title={title || (fixed_dir && active ? undefined : 'Sort')}
      >
        {label}
        {Icon ? <Icon size={10} aria-hidden="true" /> : null}
      </span>
    );
  };

  render_th = (pos, children, extra = '') => (
    <th role="columnheader" className={`${LIST_TH} ${pos} ${extra}`}>
      <div className="flex flex-col items-start gap-0.5 max-lg:flex-row max-lg:gap-3">
        {children}
      </div>
    </th>
  );

  // Type-to-search box, sized to sit level with the dropdown triggers beside it.
  // Border turns brand-coloured while a term is active, like the dropdowns.
  render_search_box = () => {
    const { search } = this.state;
    const active = !!search;
    return (
      <div
        className="max-sm:w-full"
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      >
        <FaSearch
          size={12}
          color="var(--text-muted)"
          style={{ position: 'absolute', left: 10, pointerEvents: 'none' }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => this.set_search(e.target.value)}
          // Escape clears — the same reflex as any other search field, and
          // faster than reaching for the × with the hand already on the keys.
          onKeyDown={(e) => {
            if (e.key === 'Escape' && search) this.clear_search();
          }}
          aria-label="Search tasks by student first name"
          placeholder="Search first name"
          className="!h-9 max-sm:!h-10 max-sm:!w-full"
          style={{
            width: 220,
            boxSizing: 'border-box',
            padding: '0 26px 0 28px',
            borderRadius: 8,
            fontSize: 13,
            border: '1px solid',
            borderColor: active ? 'var(--brand-primary)' : 'var(--input-border)',
            background: 'var(--input-bg)',
            color: 'var(--input-text)',
            margin: 0
          }}
        />
        {active ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear search"
            title="Clear"
            onClick={this.clear_search}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') this.clear_search();
            }}
            style={{
              position: 'absolute',
              right: 8,
              display: 'inline-flex',
              cursor: 'pointer',
              color: 'var(--text-muted)'
            }}
          >
            <FaTimes size={11} />
          </span>
        ) : null}
      </div>
    );
  };

  // Segmented control; on phones it fills the width and wraps into even cells.
  render_segmented = (options, selected, on_select, label) => (
    <div className={options.length <= 4 ? SEG_WRAP_SHORT : SEG_WRAP}>
      <Segmented options={options} value={selected} onChange={on_select} label={label} />
    </div>
  );

  // Every per-rule number box's server bounds, for RuleRow.
  rule_bounds = () => {
    const s = this.state;
    return {
      min_refresh_hours: s.gen_min_refresh_hours,
      max_refresh_hours: s.gen_max_refresh_hours,
      min_due_offset_hours: s.gen_min_due_offset_hours,
      max_due_offset_hours: s.gen_max_due_offset_hours,
      min_expiry_minutes: s.gen_min_expiry_minutes,
      max_expiry_minutes: s.gen_max_expiry_minutes,
      min_dedup_hours: s.gen_min_dedup_hours,
      max_dedup_hours: s.gen_max_dedup_hours,
      min_grace_minutes: s.gen_min_grace_minutes,
      max_grace_minutes: s.gen_max_grace_minutes,
      min_delay_minutes: s.gen_min_delay_minutes,
      max_delay_minutes: s.gen_max_delay_minutes,
      min_lookback_hours: s.gen_min_lookback_hours,
      max_lookback_hours: s.gen_max_lookback_hours,
      min_lead_minutes: s.gen_min_lead_minutes,
      max_lead_minutes: s.gen_max_lead_minutes
    };
  };

  // One row per generation rule. Non-super-users see every value but cannot change it:
  // the point of opening this tab is to find out what the generator is doing.
  render_rule_card = (rule) => {
    const { gen_min_day, gen_max_day, gen_levels, gen_priorities, saving_config, gen_can_edit } =
      this.state;
    const t = rule.task_type;
    return (
      <RuleRow
        key={t}
        rule={rule}
        levels={gen_levels}
        priorities={gen_priorities}
        locked={saving_config || !gen_can_edit}
        min_day={gen_min_day}
        max_day={gen_max_day}
        bounds={this.rule_bounds()}
        on_set={(patch) => this.set_rule(t, patch)}
        on_tier={(field, value) => this.set_rule_tier(t, field, value)}
        on_lead={(raw) => this.set_rule_lead(t, raw)}
        on_optional={(field, raw, min, max) => this.set_rule_optional(t, field, raw, min, max)}
        on_hours={(field, raw, min, max) => this.set_rule_hours(t, field, raw, min, max)}
        on_toggle_day={(day) => this.toggle_rule_day(t, day)}
        on_toggle_all_days={(all) => this.toggle_all_rule_days(t, all)}
      />
    );
  };

  // Task-generation rules: which of the rules run, how often they re-scan, and
  // on which day from their anchor date. Members, family-plan students and
  // #stop / #nomembership-flagged students are always skipped server-side.
  //
  // VISIBLE to every agent who opens the page, EDITABLE only by a SUPER_USER.
  // It used to be hidden outright from everyone else, which meant an agent
  // wondering why a task type had stopped appearing had no way to find out that
  // its rule was simply switched off. Read-only answers that; the editing gate
  // is unchanged in effect and is now enforced server-side (the save and
  // "run generation now" endpoints 403 a non-super-user) rather than by this
  // component declining to render.
  render_config_section = () => {
    const {
      gen_rules,
      saving_config,
      config_dirty,
      generating,
      gen_can_edit,
      gen_rule_notes,
      gen_globals,
      gen_globals_bounds
    } = this.state;
    const globals_invalid = has_globals_errors(validate_gen_globals(gen_globals));
    const rules_invalid = has_rule_errors(gen_rules, this.lead_bounds());
    if (!gen_rules.length) {
      return (
        <div className="px-5 py-10 text-center text-sm text-content-muted">
          Task generation rules are not available yet.
        </div>
      );
    }
    const save_title = (() => {
      if (!gen_can_edit) return 'Only super users can edit the generation config';
      if (globals_invalid) return 'Fix the global settings errors first';
      if (rules_invalid) return 'Fix the rule errors first';
      return undefined;
    })();
    const run_title = (() => {
      if (!gen_can_edit) return 'Only super users can run the generator';
      if (config_dirty) return 'Save the config first';
      return 'Run every enabled rule now, ignoring refresh rates';
    })();
    return (
      <div className="mx-[5%] my-5 flex flex-col gap-3.5 max-sm:mx-3">
        <GenPageHeader
          can_edit={gen_can_edit}
          dirty={config_dirty}
          saving={saving_config}
          generating={generating}
          save_disabled={
            saving_config || !config_dirty || !gen_can_edit || globals_invalid || rules_invalid
          }
          save_title={save_title}
          run_disabled={generating || config_dirty || !gen_can_edit}
          run_title={run_title}
          on_save={this.save_gen_config}
          on_run={this.run_generate}
        />
        {gen_globals ? (
          <GlobalSettingsCard
            globals={gen_globals}
            bounds={gen_globals_bounds}
            disabled={saving_config || !gen_can_edit}
            on_change={this.set_gen_global}
          />
        ) : null}
        <RulesSection rules={gen_rules} notes={rule_notes_lines(gen_rule_notes)}>
          {gen_rules.map(this.render_rule_card)}
        </RulesSection>
      </div>
    );
  };

  // The list response's window; /me's until the first list lands.
  call_window = () => {
    const { call_window, me } = this.state;
    if (call_window) return call_window;
    return me ? { start: me.call_window_start, end: me.call_window_end } : null;
  };

  // "Their time": the student's wall clock and whether they may be called now.
  render_local_time = (item) => {
    const cell = localTimeCell(item, Date.now(), this.call_window());
    if (!cell) return <span className="text-content-muted max-lg:hidden">—</span>;
    const eligible = cell.call === 'eligible';
    return (
      <>
        <div
          className="min-w-0 text-content-primary"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {cell.time}
          <span className="text-content-muted" title={cell.place}>
            {' '}
            · {cell.place}
          </span>
        </div>
        <div
          className={`text-[11.5px] ${
            eligible ? 'font-medium text-state-success-text' : 'text-content-muted'
          }`}
        >
          {callLabel(cell.call)}
        </div>
      </>
    );
  };

  render_due = (item, overdue) => {
    const { filter_tz } = this.props;
    const elapsed = is_instant_live(item) ? this.render_elapsed(item) : null;
    const kind = dueKind(!!elapsed, overdue);
    // A live instant task's number is how long it has waited; its due time is history.
    const value =
      elapsed ||
      shortStamp(item.due_datetime_iso, filter_tz, Date.now()) ||
      item.due_datetime ||
      '—';
    return (
      <div
        className="flex flex-col max-lg:items-end"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <span className="text-[11px] text-content-muted lg:hidden">{DUE_CAPTION[kind]}</span>
        <span
          className={kind === 'overdue' ? 'font-semibold text-state-error' : 'text-content-primary'}
        >
          {value}
        </span>
        {kind === 'due' ? null : (
          <span className="text-[11.5px] text-content-muted max-lg:hidden">
            {DUE_CAPTION[kind]}
          </span>
        )}
      </div>
    );
  };

  render_row = (item) => {
    // Terminal covers auto_closed too — those rows are done, so they get the
    // read-only "View" action and never the overdue highlight.
    const terminal = is_terminal(item);
    const overdue = is_overdue(item);
    const { me, level, show_created } = this.state;
    const { access_type, set_notification_variable, filter_tz } = this.props;
    const assignable = !terminal && can_assign(me, access_type);
    const closed_line = closed_by_line(item);
    const now = Date.now();
    // Open rows only: a closed task is not called, so muting it says nothing.
    const off = terminal ? null : offHours(item.student_timezone, now, this.call_window());
    const status = statusMeta(item.status);
    const task = taskCell(item, { show_created, tz: filter_tz, now });
    const meta = memberMeta(item);
    const person = person_column(level);
    const name = item.student_name || item.student_uuid;
    return (
      <tr
        key={item.uuid}
        role="row"
        className={`${LIST_ROW} ${off ? 'bg-surface-row-muted' : 'bg-surface-card'}`}
        title={off ? offHoursTooltip(off) : undefined}
        data-off-hours={off ? 'true' : undefined}
      >
        <td role="cell" className={`${LIST_TD} ${POS.pri.card} ${POS.pri.lg}`}>
          <div className="flex items-start gap-1 lg:flex-col">
            <PriorityBadge item={item} />
            {item.is_instant ? (
              <span
                className="whitespace-nowrap rounded-full bg-state-warning-bg px-1.5 py-px text-[10px] font-bold leading-4 text-state-warning-text max-lg:px-2 max-lg:py-0.5 max-lg:text-[11px] max-lg:font-semibold"
                title={short_label(item.task_type, item.task_label)}
              >
                <span className="lg:hidden">Instant</span>
                <span className="max-lg:hidden">INST</span>
              </span>
            ) : null}
          </div>
        </td>
        <td role="cell" className={`${LIST_TD} ${POS.member.card} ${POS.member.lg}`}>
          {/* A lead task carries the LEAD uuid in student_uuid — there is no
              student row behind it — so the profile link must not be built from it. */}
          {item.student_uuid && !item.is_lead ? (
            <Link
              to={`/studentprofile?id=${encodeURIComponent(item.student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate font-semibold text-content-link hover:underline"
              title={name}
            >
              {name}
            </Link>
          ) : (
            <span
              className="block truncate font-semibold text-content-primary"
              title={item.student_name || undefined}
            >
              {item.student_name || '—'}
            </span>
          )}
          {meta ? <div className="text-[11.5px] text-content-muted">{meta}</div> : null}
        </td>
        <td role="cell" className={`${LIST_TD} ${POS.task.card} ${POS.task.lg}`}>
          <div
            className="overflow-hidden font-medium text-content-primary [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]"
            title={task.rule ? `${task.label} · ${task.rule}` : task.label}
          >
            {task.label}
          </div>
          {task.rule ? (
            <div className="text-[11.5px] text-content-muted" data-testid="sales-task-rule-line">
              {task.rule}
            </div>
          ) : null}
          {show_created ? (
            <div className="text-[11.5px] text-content-muted" data-testid="sales-task-created-line">
              {createdListLine(item.created_at_iso, filter_tz, now) || item.created_date_iso || '—'}
            </div>
          ) : null}
        </td>
        <td role="cell" className={`${LIST_TD} ${POS.due.card} ${POS.due.lg}`}>
          {this.render_due(item, overdue)}
        </td>
        <td role="cell" className={`${LIST_TD} ${POS.local.card} ${POS.local.lg}`}>
          {this.render_local_time(item)}
        </td>
        <td role="cell" className={`${LIST_TD} ${POS.status.card} ${POS.status.lg}`}>
          <div className="flex flex-col items-start gap-0.5">
            <Chip tone={status.tone}>{status.label}</Chip>
            {closed_line ? (
              <span className="text-[11.5px] text-content-muted">{closed_line}</span>
            ) : null}
          </div>
        </td>
        <td role="cell" className={`${LIST_TD} ${POS.person.card} ${POS.person.lg} break-words`}>
          {/* Card mode captions the holder via ::before so the label text is not duplicated. */}
          <div
            data-label={person.label}
            className="text-content-secondary max-lg:before:mr-1 max-lg:before:text-[11.5px] max-lg:before:text-content-muted max-lg:before:content-[attr(data-label)]"
          >
            {this.render_person(item)}
          </div>
          {this.render_allocation_chip(item, terminal, now)}
        </td>
        <td role="cell" className={`${LIST_TD} ${POS.action.card} ${POS.action.lg}`}>
          <div className={ACTION_STACK}>
            <Link
              to={this.form_link(item)}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:no-underline max-lg:!h-10"
              style={btnStyle('outline', 'row')}
            >
              {terminal ? 'View' : 'Open task'}
            </Link>
            {assignable ? (
              <AssignPicker
                task_uuid={item.uuid}
                on_assigned={this.on_assigned}
                on_error={(m) => set_notification_variable(true, 'error', m)}
              />
            ) : null}
          </div>
        </td>
      </tr>
    );
  };

  // Session tasks: who joins the session's last 10 minutes (PROD-9977).
  render_allocation_chip = (item, terminal, now) => {
    const { filter_tz } = this.props;
    const chip = allocationChip(item, this.current_cms_uuid, resolveFilterTz(filter_tz), {
      terminal,
      now
    });
    if (!chip) return null;
    return (
      <div className="mt-1" data-testid="sales-task-allocation-chip">
        <Chip tone={chip.tone}>{chip.label}</Chip>
      </div>
    );
  };

  // Placeholder rows shaped like the table while the first page loads.
  render_loading_rows = () => (
    <>
      {[0, 1, 2, 3].map((i) => (
        <tr
          key={`skeleton-${i}`}
          role="row"
          className="block border-x-0 border-b-0 border-t border-line-subtle"
        >
          <td role="cell" colSpan={LIST_COLS} className="block px-4 py-3.5">
            <div className="grid grid-cols-[40px_minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(0,1fr)_80px] items-center gap-3.5 max-lg:grid-cols-[40px_minmax(0,1fr)_80px]">
              <span className="h-[18px] rounded-full bg-surface-sunken" />
              <span className="flex flex-col gap-1.5">
                <span className="h-3 w-4/5 rounded bg-surface-sunken" />
                <span className="h-2.5 w-1/2 rounded bg-surface-sunken" />
              </span>
              <span className="h-3 w-3/4 rounded bg-surface-sunken max-lg:hidden" />
              <span className="h-3 w-4/5 rounded bg-surface-sunken max-lg:hidden" />
              <span className="h-7 rounded-[7px] bg-surface-sunken" />
            </div>
          </td>
        </tr>
      ))}
      <tr role="row" className="block border-x-0 border-b-0 border-t border-line-subtle">
        <td
          role="cell"
          colSpan={LIST_COLS}
          className="block px-4 py-2 text-left text-xs text-content-muted"
        >
          Loading tasks…
        </td>
      </tr>
    </>
  );

  render_empty = (summary) => (
    <tr role="row" className="block border-0">
      <td role="cell" colSpan={LIST_COLS} className="block px-6 py-9">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="text-[15px] font-semibold text-content-primary">
            No tasks match these filters
          </div>
          <div className="max-w-[420px] text-content-secondary">{summary}</div>
          <Btn
            variant="outline"
            onClick={this.clear_filters}
            style={{ marginTop: 6 }}
            data-testid="sales-task-clear-filters"
          >
            Clear filters
          </Btn>
        </div>
      </td>
    </tr>
  );

  render_body = (rows, summary) => {
    const { loading, load_error } = this.state;
    const state = bodyState(loading, rows.length, load_error);
    if (state === 'loading') return this.render_loading_rows();
    // The banner above the table carries the failure and its Retry.
    if (state === 'error') return null;
    if (state === 'empty') return this.render_empty(summary);
    const { sort_key, level } = this.state;
    const runs =
      sort_key === 'priority'
        ? group_breaks(rows)
        : sort_breaks(rows, SORT_LABELS[sort_key] || person_column(level).label);
    const breaks = new Map(runs.map((b) => [b.index, b]));
    const body = [];
    rows.forEach((item, idx) => {
      const band = breaks.get(idx);
      if (band) {
        body.push(
          <tr
            key={`group-${item.uuid}`}
            role="row"
            className="block border-x-0 border-b-0 border-t border-line-subtle bg-surface-row-muted"
          >
            <td
              role="cell"
              colSpan={LIST_COLS}
              className="flex items-center gap-2 px-4 py-1.5 max-lg:px-3"
            >
              <span className={LABEL_CLASS}>{band.label}</span>
              <span className="text-[11px] font-semibold text-content-muted">
                {bandCountLabel(band.count)}
              </span>
            </td>
          </tr>
        );
      }
      body.push(this.render_row(item));
    });
    return body;
  };

  // The report tab needs gen_can_edit, which lands with the config GET, so
  // ?tab=report shows Tasks until then; the URL is left alone so it resolves.
  can_view_report = () => {
    const { gen_can_edit } = this.state;
    return gen_can_edit;
  };

  // Client-side gate only; the server enforces SUPER_USER on role membership
  // and can_edit_team on roster edits.
  can_manage_sales_users = () => {
    const { access_type } = this.props;
    const { me } = this.state;
    return hasAccess(access_type, 'SUPER_USER') || !!me?.can_edit_team;
  };

  // Client gate only; the allocation endpoints 403 everyone else.
  can_allocate_sessions = () => {
    const { access_type } = this.props;
    const { me } = this.state;
    return canAllocateSessions(me, hasAccess(access_type, 'SUPER_USER'));
  };

  // Client gate only; the server scopes My Leads to the caller and 403s ?ops_uuid= for non-managers.
  leads_access = () => {
    const { access_type } = this.props;
    const { me } = this.state;
    const super_user = hasAccess(access_type, 'SUPER_USER');
    return {
      mine: canSeeMyLeads(me, super_user, hasAccess(access_type, 'SALES')),
      team: canSeeTeamLeads(me, super_user)
    };
  };

  notify = (type, message) => {
    const { set_notification_variable } = this.props;
    set_notification_variable(true, type, message);
  };

  active_tab = () => {
    const { location } = this.props;
    return resolveTab(
      location?.search || '',
      this.can_view_report(),
      this.can_manage_sales_users(),
      this.can_allocate_sessions(),
      this.leads_access()
    );
  };

  // Old ?tab=advisors&advisor=X links (goserver 409 guard) now live on the advisor's own page.
  // Runs once the tab resolves to advisors, i.e. after /me grants manage rights.
  redirect_advisor_link = () => {
    const { location, history } = this.props;
    if (!location || !history || this.active_tab() !== 'advisors') return;
    const uuid = advisorFromSearch(location.search || '');
    if (!uuid || this.advisor_redirected === uuid) return;
    this.advisor_redirected = uuid;
    history.replace(advisorPagePath(uuid));
  };

  select_tab = (tab) => {
    const { location, history } = this.props;
    if (!location || !history) return;
    const search = searchWithTab(location.search || '', tab);
    if (search === (location.search || '')) return;
    history.replace({ pathname: location.pathname, search });
  };

  on_tab_key = (e) => {
    const tabs = tabList(
      this.can_view_report(),
      this.can_manage_sales_users(),
      this.can_allocate_sessions(),
      this.leads_access()
    );
    const next = tabForKey(tabs, this.active_tab(), e.key);
    if (!next) return;
    e.preventDefault();
    this.select_tab(next);
    const el = document.getElementById(`sales-tab-${next}`);
    if (el) el.focus();
  };

  // Page title and section tabs share one row; the CMS's big title card is dropped to save height.
  render_tab_strip = (active) => (
    <div className="mx-[5%] mt-5 flex flex-wrap items-end gap-x-8 gap-y-1 border-b border-line-subtle max-sm:mx-3">
      <h1
        className="m-0 pb-2 font-semibold text-content-primary"
        style={{ fontSize: 18, lineHeight: '26px' }}
      >
        Sales Task List
      </h1>
      <div
        role="tablist"
        aria-label="Sales task list sections"
        className="flex flex-wrap items-center gap-x-5"
      >
        {tabList(
          this.can_view_report(),
          this.can_manage_sales_users(),
          this.can_allocate_sessions(),
          this.leads_access()
        ).map((t) => {
          const selected = t.id === active;
          return (
            <button
              key={t.id}
              id={`sales-tab-${t.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`sales-tabpanel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => this.select_tab(t.id)}
              onKeyDown={this.on_tab_key}
              className="whitespace-nowrap text-[13px] transition-colors"
              // Inline overrides beat the host CMS's global `button {}` rule.
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${selected ? 'var(--brand-primary)' : 'transparent'}`,
                borderRadius: 0,
                margin: 0,
                marginBottom: -1,
                padding: '8px 0 9px',
                fontWeight: selected ? 600 : 400,
                color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  render_level_control = () => {
    const { me, me_loaded, level } = this.state;
    const { access_type } = this.props;
    if (!me_loaded) {
      return (
        <div
          className="inline-flex rounded-lg animate-pulse"
          aria-busy="true"
          aria-label="Loading level"
          style={{ background: 'var(--surface-sunken)', width: 160, height: 34 }}
        />
      );
    }
    return this.render_segmented(level_options(me, access_type), level, this.set_level, 'Level');
  };

  // The rule line above the table: how the rows are ordered, and the one timezone note.
  render_list_bar = () => {
    const { sort_key, sort_dir, items } = this.state;
    const { filter_tz, tz_from_header } = this.props;
    const hints = [];
    let pinned = false;
    if (sort_key === 'priority') {
      hints.push('Sorted by priority: instant and P0 first, then P1 and P2, newest first in each.');
    }
    if (sort_key === 'created') {
      hints.push(`Sorted by created date, ${sort_dir === 'desc' ? 'newest' : 'oldest'} first.`);
    }
    if (sort_key !== 'priority' && items.some(is_instant_live)) {
      pinned = true;
    }
    return (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-line-subtle bg-surface-sunken px-4 py-2.5 text-xs text-content-secondary max-lg:px-3">
        {hints.map((h) => (
          <span key={h}>{h}</span>
        ))}
        {pinned ? (
          <span className="font-semibold text-state-warning-text" data-testid="sales-task-pin-hint">
            Open instant tasks stay on top whatever the sort.
          </span>
        ) : null}
        <span
          className="lg:ml-auto"
          title={resolveFilterTz(filter_tz)}
          data-testid="sales-task-date-tz"
        >
          Due and created in{' '}
          <b className="font-semibold text-content-primary">{zoneLabel(filter_tz, Date.now())}</b>
          {tz_from_header
            ? ', set in the top bar.'
            : '. Pick a timezone in the top bar to change it.'}{' '}
          Their time is the student&apos;s own.
        </span>
      </div>
    );
  };

  render_load_error = () => {
    const { load_error, loading } = this.state;
    if (!load_error) return null;
    return (
      <div className="px-4 pt-3 max-lg:px-3">
        <Banner
          tone="error"
          role="alert"
          testId="sales-task-load-error"
          actions={
            <Btn variant="outline" size="row" onClick={this.reload} disabled={loading}>
              Retry
            </Btn>
          }
        >
          <span className="font-semibold">Couldn&apos;t load tasks.</span> {load_error}. Your
          filters are kept.
        </Banner>
      </div>
    );
  };

  render_pages = (page, page_limit) => {
    const { total } = this.state;
    const range = pageRange(page, this.page_size, total);
    if (!range) return null;
    return (
      <nav
        aria-label="Pages"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-b-[10px] border-t border-line-subtle bg-surface-row-muted px-4 py-2.5 text-xs text-content-secondary max-lg:px-3"
      >
        <span>
          Showing{' '}
          <b className="font-semibold text-content-primary">
            {range.from}–{range.to}
          </b>{' '}
          of {total}
          {page_limit > 1 ? ` · page ${page} of ${page_limit}` : ''}
        </span>
        {page_limit > 1 ? (
          <div className="ml-auto">
            <Paginator curr_page={page} page_limit={page_limit} pageChange={this.set_page} />
          </div>
        ) : null}
      </nav>
    );
  };

  toggle_filters = () =>
    this.setState(
      (st) => ({ filters_collapsed: !st.filters_collapsed }),
      () => writeFiltersCollapsed(this.state.filters_collapsed)
    );

  render_list_header = (summary) => {
    const {
      filters_collapsed,
      loading,
      date_from,
      date_to,
      show_created,
      created_from,
      created_to,
      date_field,
      status_filter,
      type_filter,
      level,
      scope_filter,
      owner_filter,
      call_filter,
      total
    } = this.state;
    const row = 'flex flex-wrap items-center gap-2.5 max-sm:flex-col max-sm:items-stretch';
    return (
      <div className="flex flex-col gap-3 border-b border-line-subtle px-5 py-3.5 max-sm:px-3">
        <div className="flex items-center gap-2.5">
          <h2 className="m-0 text-[18px] font-semibold leading-6 text-content-primary">Tasks</h2>
          <span className="text-xs text-content-muted">
            {total} {total === 1 ? 'item' : 'items'}
            {total > this.page_size
              ? ` · page ${Math.min(this.state.page, Math.ceil(total / this.page_size))} of ${Math.ceil(total / this.page_size)}`
              : ''}
          </span>
          <button
            type="button"
            onClick={this.load_data}
            disabled={loading}
            aria-label="Refresh sales tasks"
            className="max-sm:ml-auto max-sm:!h-[34px] max-sm:!w-[34px]"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 28,
              width: 28,
              borderRadius: 9999,
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-card)',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
              padding: 0,
              margin: 0
            }}
          >
            <FaSyncAlt
              size={13}
              color="var(--text-link-brand)"
              className={loading ? 'animate-spin' : ''}
            />
          </button>
          {filters_collapsed ? (
            <span
              className="min-w-0 flex-1 truncate text-xs text-content-secondary max-sm:hidden"
              title={summary}
              data-testid="sales-task-filter-summary"
            >
              {summary}
            </span>
          ) : null}
          <Btn
            size="row"
            onClick={this.toggle_filters}
            aria-expanded={!filters_collapsed}
            aria-controls="sales-task-filters"
            data-testid="sales-task-filters-toggle"
            style={{ marginLeft: filters_collapsed ? 0 : 'auto' }}
          >
            {filters_collapsed ? 'Show filters' : 'Hide filters'}
            <span aria-hidden="true">{filters_collapsed ? '▾' : '▴'}</span>
          </Btn>
        </div>
        {filters_collapsed ? (
          <p className="m-0 hidden text-xs text-content-secondary max-sm:block">{summary}</p>
        ) : (
          <div id="sales-task-filters" className="flex flex-col gap-3">
            <div className={row}>
              {this.render_search_box()}
              <div
                className="inline-flex flex-wrap items-center gap-2 max-sm:w-full"
                data-testid="sales-task-date-filter"
              >
                {show_created
                  ? this.render_segmented(
                      DATE_FIELDS,
                      date_field,
                      this.set_date_field,
                      'Filter dates by'
                    )
                  : null}
                <DateRangeFilter
                  from={date_field === 'created' ? created_from : date_from}
                  to={date_field === 'created' ? created_to : date_to}
                  onChange={this.set_picked_range}
                  onClear={this.clear_picked_range}
                />
              </div>
              {/* Inline box metrics: the global label style would stretch it past the filters' height. */}
              <label
                className="ml-auto inline-flex cursor-pointer select-none items-center gap-2 rounded-lg border border-line-subtle text-content-primary max-sm:ml-0 max-sm:!h-10"
                style={{
                  height: 36,
                  margin: 0,
                  padding: '0 12px',
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: '20px',
                  // The global label style is bold uppercase; this one reads like the other filters.
                  textTransform: 'none',
                  letterSpacing: 'normal'
                }}
                data-testid="sales-task-show-created"
              >
                <input
                  type="checkbox"
                  className="m-0"
                  checked={show_created}
                  onChange={(e) => this.set_show_created(e.target.checked)}
                />
                Show created date
              </label>
            </div>
            <div className={row}>
              {this.render_level_control()}
              {level
                ? this.render_segmented(
                    scope_options(level),
                    normalize_scope(scope_filter, level),
                    this.set_scope_filter,
                    'Claim'
                  )
                : null}
              {this.render_segmented(
                STATUS_FILTERS,
                status_filter,
                this.set_status_filter,
                'Status'
              )}
            </div>
            <div className={row}>
              {/* Native select drawn like the MultiSelectDropdown triggers: own chevron, same padding. */}
              <div className="inline-flex items-center gap-2 max-sm:w-full">
                <label
                  htmlFor="sales-task-call-filter"
                  className="whitespace-nowrap text-xs text-content-muted"
                  style={{ margin: 0, fontWeight: 400, textTransform: 'none' }}
                >
                  Call eligibility
                </label>
                <span className="relative inline-flex items-center max-sm:flex-1">
                  <select
                    id="sales-task-call-filter"
                    name="call_filter"
                    value={call_filter}
                    onChange={(e) => this.set_call_filter(e.target.value)}
                    // index.css forces select { height: 46px !important }; match the 34px triggers.
                    className="appearance-none cursor-pointer !h-[34px] max-sm:!h-10 max-sm:!w-full"
                    style={{
                      width: 150,
                      boxSizing: 'border-box',
                      margin: 0,
                      padding: '6px 30px 6px 10px',
                      fontSize: 13,
                      lineHeight: '20px',
                      borderRadius: 8,
                      border: '1px solid',
                      borderColor: call_filter ? 'var(--brand-primary)' : 'var(--input-border)',
                      background: 'var(--input-bg)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="">Any</option>
                    {CALL_FILTERS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown
                    aria-hidden="true"
                    size={11}
                    className="pointer-events-none absolute"
                    style={{ right: 10, color: 'var(--text-muted)' }}
                  />
                </span>
              </div>
              {level === 'L3' ? (
                <div className={DROPDOWN_WRAP}>
                  <MultiSelectDropdown
                    label="Owner"
                    options={this.owner_options()}
                    selected={owner_filter}
                    onChange={this.set_owner_filter}
                    onClear={() => this.set_owner_filter([])}
                    placeholder="All owners"
                  />
                </div>
              ) : null}
              <div className={DROPDOWN_WRAP}>
                <MultiSelectDropdown
                  label="Task type"
                  options={TYPE_OPTIONS}
                  selected={type_filter}
                  onChange={this.set_type_filter}
                  onClear={() => this.set_type_filter([])}
                  placeholder="All types"
                  width={210}
                />
              </div>
            </div>
            <p
              className="m-0 border-t border-line-subtle pt-2.5 text-xs text-content-secondary"
              data-testid="sales-task-filter-summary"
            >
              {summary}
            </p>
          </div>
        )}
      </div>
    );
  };

  render_list_table = (rows, summary) => {
    const { show_created, level } = this.state;
    const person = person_column(level);
    return (
      <table
        ref={this.table_ref}
        role="table"
        aria-label="Sales tasks"
        className="block w-full text-[13px]"
      >
        <thead
          role="rowgroup"
          className="block border-x-0 border-t-0 border-b border-line-subtle bg-surface-sunken"
        >
          <tr role="row" className={LIST_HEAD_ROW}>
            {this.render_th(
              POS.pri.lg,
              this.render_sort_label('Pri', 'priority', { fixed_dir: true, title: 'Priority' })
            )}
            {this.render_th(POS.member.lg, this.render_sort_label('Member', 'member'))}
            {/* Task sorts by task_type server-side, which groups rows of the same rule. */}
            {this.render_th(POS.task.lg, [
              this.render_sort_label('Task', 'task'),
              ...(show_created
                ? [this.render_sort_label('Created', 'created', { minor: true })]
                : [])
            ])}
            {this.render_th(POS.due.lg, this.render_sort_label('Due', 'due_datetime'))}
            {this.render_th(POS.local.lg, this.render_sort_label('Their time', 'local_time'))}
            {this.render_th(POS.status.lg, this.render_sort_label('Status', 'status'))}
            {this.render_th(POS.person.lg, [
              this.render_sort_label(person.label, person.sort_key),
              ...(person.sub_label
                ? [
                    <span
                      key="sub"
                      className="text-[11px] font-semibold text-content-muted max-lg:hidden"
                    >
                      {person.sub_label}
                    </span>
                  ]
                : [])
            ])}
            {/* No data to sort by; its label just mirrors Status. */}
            {this.render_th(
              POS.action.lg,
              <span className="w-full text-right text-[10.5px] font-bold uppercase tracking-[.04em] text-content-muted">
                Action
              </span>,
              'max-lg:hidden'
            )}
          </tr>
        </thead>
        <tbody
          role="rowgroup"
          className="block [&>tr:first-child]:border-t-0"
          aria-busy={this.state.loading || undefined}
        >
          {this.render_body(rows, summary)}
        </tbody>
      </table>
    );
  };

  render() {
    const { total, me, me_loaded, level, scope_filter, owner_filter } = this.state;
    const { location, filter_tz } = this.props;
    const active = this.active_tab();
    const leads = this.leads_access();
    if (leadsTabPending(location?.search || '', active, me_loaded)) {
      return (
        <div className="stl-root">
          <CommonHeader title="Sales Task List" title_only />
          <LeadsLoading />
        </div>
      );
    }
    // items is already the current server page; total drives the paginator.
    const paged_rows = this.state.items;
    const page_limit = Math.max(1, Math.ceil(total / this.page_size));
    const page = Math.min(this.state.page, page_limit);
    const summary = filter_summary(
      {
        ...this.state,
        // Same level-normalised scope and owner the /list query sends.
        scope_filter: normalize_scope(scope_filter, level),
        owner_filter: level === 'L3' ? owner_filter : []
      },
      {
        tz_abbr: filterTzAbbr(filter_tz),
        type_label: (v) => TYPE_LABELS.get(v) || v
      }
    );

    return (
      <div className="stl-root">
        <CommonHeader title="Sales Task List" title_only />

        {this.render_tab_strip(active)}

        {leads.mine ? (
          <TabPanel id="my_leads" active={active} idPrefix="sales">
            {active === 'my_leads' ? <MyLeadsPage onRoster={!!level_from_me(me)} /> : null}
          </TabPanel>
        ) : null}

        {leads.team ? (
          <TabPanel id="team_leads" active={active} idPrefix="sales">
            {active === 'team_leads' ? <TeamLeadsPage /> : null}
          </TabPanel>
        ) : null}

        {this.can_allocate_sessions() ? (
          <TabPanel id="session_allocation" active={active} idPrefix="sales">
            {active === 'session_allocation' ? (
              <SessionAllocationPanel notify={this.notify} />
            ) : null}
          </TabPanel>
        ) : null}

        <TabPanel id="generation" active={active} idPrefix="sales">
          {active === 'generation' ? this.render_config_section() : null}
        </TabPanel>

        {/* Reporting is SUPER_USER-only (product decision Q14), gated on the
            same server-reported capability as the generation card's editing.
            Kept mounted once allowed so its range and results survive tab
            switches; it loads on first open. */}
        {this.can_view_report() ? (
          <TabPanel id="report" active={active} idPrefix="sales">
            <InstantReport can_view open={active === 'report'} tz={filter_tz} />
          </TabPanel>
        ) : null}

        {this.can_manage_sales_users() ? (
          <TabPanel id="sales_users" active={active} idPrefix="sales">
            {active === 'sales_users' ? (
              <SalesUsersPanel notify={this.notify} self_uuid={this.current_cms_uuid} />
            ) : null}
          </TabPanel>
        ) : null}

        {this.can_manage_sales_users() ? (
          <TabPanel id="advisors" active={active} idPrefix="sales">
            {/* An ?advisor= link is being replaced onto the advisor page; skip the summary load. */}
            {active === 'advisors' && !advisorFromSearch(location?.search || '') ? (
              <AdvisorsPanel notify={this.notify} canWrite={canWriteAdvisors(me)} />
            ) : null}
          </TabPanel>
        ) : null}

        <TabPanel id="tasks" active={active} idPrefix="sales">
          <section className="mx-[5%] my-5 rounded-[10px] border border-line-subtle bg-surface-card max-sm:mx-3">
            {this.render_list_header(summary)}
            {this.render_list_bar()}
            {this.render_load_error()}
            {this.render_list_table(paged_rows, summary)}
            {this.render_pages(page, page_limit)}
          </section>
        </TabPanel>
      </div>
    );
  }
}

SalesTaskList.propTypes = {
  set_notification_variable: PropTypes.func.isRequired,
  access_type: PropTypes.string,
  filter_tz: PropTypes.string,
  tz_from_header: PropTypes.bool,
  location: PropTypes.shape({ pathname: PropTypes.string, search: PropTypes.string }),
  history: PropTypes.shape({ replace: PropTypes.func })
};

SalesTaskList.defaultProps = {
  access_type: '',
  filter_tz: undefined,
  tz_from_header: false,
  location: undefined,
  history: undefined
};

// access_type only decides whether the ALL level and the Sales users tab are
// offered; the server enforces both. Generation-card editability comes from the config endpoint.
// filter_tz is the zone the date filters and due times are read in: IST until
// the user picks a header timezone, then the header's.
const mapStateToProps = (state) => ({
  access_type: state.home.access_type,
  filter_tz: pageFilterTz(state.home.iana_timezone, state.home.tz_user_selected),
  tz_from_header: !!state.home.tz_user_selected
});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(SalesTaskList);
